import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Mail, 
  MessageCircle, 
  BookOpen, 
  FileText, 
  Clock,
  CheckCircle,
  ArrowRight,
  Send,
  HelpCircle,
  Lightbulb,
  Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { AUTH_LAMBDA_FUNCTION_URL } from '../../utils/authFallback';

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiryType: 'Support'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const faqItems = [
    {
      question: 'How to send or approve invoices',
      answer: 'To send an invoice, go to the "Send Invoice" section, fill in the details, and click send. For approvals, invoices are automatically approved after 7 days, or you can manually approve them in the "Received Invoices" section.'
    },
    {
      question: 'Payment scheduling and notifications',
      answer: 'You can schedule payments to be automatically collected on the invoice due date. You\'ll receive notifications when payments are processed, and customers get reminders before the due date.'
    },
    {
      question: 'Managing users & roles',
      answer: 'In the Pro plan, you can add up to 4 additional users. Go to Settings > User Management to invite team members and assign roles. Each user gets their own login and can access shared invoices.'
    },
    {
      question: 'Security & Stripe payments',
      answer: 'All payments are processed securely through Stripe with bank-level encryption. Your financial data is protected with enterprise-grade security and GDPR compliance.'
    },
    {
      question: 'Troubleshooting common issues',
      answer: 'If you\'re having trouble, try refreshing the page, clearing your browser cache, or checking your internet connection. For payment issues, verify your Stripe connection in Settings.'
    },
    {
      question: 'How does the customer chat work?',
      answer: 'The built-in chat allows you to communicate directly with your customers about invoices. Customers can ask questions, request changes, or discuss payment terms. All chat messages are linked to specific invoices for easy reference.'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        message: formData.message.trim(),
        inquiryType: formData.inquiryType,
      };

      // Call auth Lambda Function URL directly (public endpoint, no auth required)
      console.log('[SupportForm] Using direct Lambda Function URL (new code v2):', AUTH_LAMBDA_FUNCTION_URL);
      const response = await fetch(AUTH_LAMBDA_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-http-method': 'POST',
          'x-http-path': '/support/contact',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Failed to parse support response');
      }

      if (response.ok && data?.success) {
        setIsSubmitted(true);

        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            company: '',
            message: '',
            inquiryType: 'Support',
          });
        }, 3000);
      } else {
        setSubmitError(
          data?.error ||
            'We could not send your message. Please email receevit@outlook.com directly.'
        );
      }
    } catch (error) {
      console.error('Support form submission error:', error);
      setSubmitError('We could not send your message. Please email receevit@outlook.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Support & Help Center | Receevit®</title>
        <meta name="description" content="Get help with Receevit® invoice management. FAQs, documentation, contact support. Fast response times. We're here to help your business succeed." />
        <link rel="canonical" href="https://www.receevit.com/support" />
        <meta property="og:title" content="Support & Help Center | Receevit®" />
        <meta property="og:description" content="Get help with Receevit® invoice management. FAQs, documentation, contact support. Fast response times. We're here to help your business succeed." />
        <meta property="og:url" content="https://www.receevit.com/support" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Support & Help Center | Receevit®",
            "url": "https://www.receevit.com/support",
            "description": "Get help with Receevit® invoice management. FAQs, documentation, contact support.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Support",
                "item": "https://www.receevit.com/support"
              }
            ]
          })}
        </script>
        
        {/* FAQ Schema for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })}
        </script>
      </Helmet>
      
      <div className="pt-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[#1F11ED] py-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold text-white mb-6"
          >
            We're Here to{' '}
            <span className="bg-gradient-to-r from-blue-300 to-white bg-clip-text text-transparent">
              Help
            </span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto"
          >
            Whether you're exploring Receevit® or already using it, our team is ready to assist. 
            Find answers in our FAQ or reach out directly — we've got you covered.
          </motion.p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Contact Options</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Choose the way that works best for you
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Combined Contact Section */}
          <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
                viewport={{ once: true }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8"
          id="contact"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-[#1F11ED]" aria-label="Contact support" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Get in Touch</h3>
                <p className="text-blue-100 mb-4">Send us a message or email us directly</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="mailto:receevit@outlook.com"
                    className="inline-flex items-center bg-white text-[#1F11ED] px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300"
                  >
                    <Mail className="w-5 h-5 mr-2" aria-label="Email icon" />
                    receevit@outlook.com
                  </a>
                  <span className="text-blue-200 hidden sm:block">or</span>
                  <span className="text-blue-200 sm:hidden">or use the form below</span>
                </div>
              </div>

              {isSubmitted ? (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" aria-label="Success checkmark" />
                  <h4 className="text-xl font-bold text-white mb-2">Message Sent!</h4>
                  <p className="text-blue-100">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                  </div>
                  
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                  </div>
                  
                  <div>
                    <input
                      type="text"
                      name="company"
                      placeholder="Company (Optional)"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                  </div>
                  
                  <div>
                    <select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                    >
                      <option value="Support" className="bg-gray-800">Support</option>
                      <option value="Sales" className="bg-gray-800">Schedule Demo (Sales)</option>
                      <option value="Other" className="bg-gray-800">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white text-[#1F11ED] py-3 px-6 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1F11ED] mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" aria-label="Send icon" />
                        Send Message
                      </>
                    )}
                  </button>
                  {submitError && (
                    <p className="mt-3 text-sm text-red-200">
                      {submitError}
                    </p>
                  )}
                </form>
              )}
              </motion.div>
          </div>
        </div>
      </section>

      {/* Self-Service / FAQ */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">FAQ</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              For quick answers, check our FAQ section below
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6"
              >
                <div className="flex items-start space-x-3">
                  <HelpCircle className="w-6 h-6 text-blue-300 mt-1 flex-shrink-0" aria-label="FAQ question" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.question}</h3>
                    <p className="text-blue-100 text-sm">{item.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Response Expectations */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-[#1F11ED]" aria-label="Response time" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Response Expectations</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              We respond to all inquiries within 24 hours on business days. Your questions are important to us, 
              and we'll get back to you as quickly as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Feedback / Feature Requests */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Lightbulb className="w-8 h-8 text-[#1F11ED]" aria-label="Feature ideas" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Feedback / Feature Requests</h2>
            <p className="text-xl text-blue-100 mb-6 max-w-3xl mx-auto">
              Have an idea to make Receevit® even better? Let us know!
            </p>
            <p className="text-blue-100 mb-8">
              Use the contact form above and select "Other" as your inquiry type to share your feedback or feature requests.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center bg-white text-[#1F11ED] px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5 mr-2" aria-label="Message icon" />
              Share Your Ideas
            </a>
          </motion.div>
        </div>
      </section>

      {/* Invoice Lookup */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Invoice Lookup</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Looking for a specific invoice? Use our lookup tool to find and view your invoices quickly.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-[#1F11ED]" aria-label="Invoice document" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Find Your Invoice</h3>
            <p className="text-blue-100 mb-8 text-center max-w-2xl mx-auto">
              Enter your invoice details to quickly locate and view any invoice sent through Receevit®.
            </p>
            <div className="text-center">
              <Link
                to="/lookup"
                className="inline-flex items-center bg-white text-[#1F11ED] px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 text-lg"
              >
                <FileText className="w-5 h-5 mr-2" aria-label="Invoice icon" />
                Look Up Invoice
                <ArrowRight className="w-5 h-5 ml-2" aria-label="Arrow right" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust & Security Reminder */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-[#1F11ED]" aria-label="Security shield" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Trust & Security Reminder</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              All support communications are encrypted and secure. Payments and sensitive client information 
              are always protected via Stripe.
            </p>
          </motion.div>
        </div>
      </section>
      </div>
    </>
  );
};

export default SupportPage;